import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingCTA from "@/components/FloatingCTA";
import LeadFormCompact from "@/components/LeadFormCompact";

/**
 * SEOPageLayout - shared layout for SEO/blog pages.
 * Pass: heroEyebrow, heroH1, heroSub, heroImage, heroImageAlt, ctaPrimary, children, lastUpdated
 */
export default function SEOPageLayout({
  heroEyebrow,
  heroH1,
  heroSub,
  heroImage,
  heroImageAlt,
  children,
  showLeadForm = true,
  breadcrumbs = [],
}) {
  return (
    <main className="bg-zinc-950 text-white min-h-screen overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt={heroImageAlt}
            className="w-full h-full object-cover opacity-25"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-950/85 to-zinc-950" />
          <div className="absolute inset-0 tribal-pattern opacity-50" />
        </div>

        <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-12">
          {breadcrumbs.length > 0 && (
            <nav
              data-testid="breadcrumbs"
              aria-label="Breadcrumb"
              className="mb-6 text-xs uppercase tracking-[0.2em] text-zinc-500"
            >
              {breadcrumbs.map((c, i) => (
                <span key={c.href || i}>
                  {c.href ? (
                    <Link to={c.href} className="hover:text-teal-400">
                      {c.label}
                    </Link>
                  ) : (
                    <span className="text-zinc-300">{c.label}</span>
                  )}
                  {i < breadcrumbs.length - 1 && <span className="mx-2">/</span>}
                </span>
              ))}
            </nav>
          )}

          {heroEyebrow && (
            <div className="inline-flex items-center gap-2 border border-teal-400/30 bg-teal-400/5 px-3 py-1.5 mb-6">
              <span className="text-xs uppercase tracking-[0.2em] text-teal-300 font-semibold">
                {heroEyebrow}
              </span>
            </div>
          )}

          <h1 className="font-display uppercase font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] max-w-4xl">
            {heroH1}
          </h1>

          {heroSub && (
            <p className="mt-6 text-zinc-300 text-base md:text-xl max-w-3xl leading-relaxed">
              {heroSub}
            </p>
          )}

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#lead-form"
              data-testid="page-cta-design"
              className="btn-primary inline-flex items-center gap-2"
            >
              Get a Free Design
              <ArrowRight size={18} />
            </a>
            <a
              href="#lead-form"
              data-testid="page-cta-quote"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="max-w-[860px] mx-auto px-6 md:px-12 pb-20 prose-tribal">
        {children}
      </article>

      {/* Lead form */}
      {showLeadForm && (
        <section id="lead-form" className="py-20 md:py-24 border-y border-zinc-900 bg-zinc-950">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="text-xs uppercase tracking-[0.3em] text-teal-400 font-semibold">
                Quick enquiry
              </p>
              <h2 className="mt-3 font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95]">
                Get your <span className="text-teal-400">free design</span>
              </h2>
              <p className="mt-5 text-zinc-400 leading-relaxed">
                Tell us a few details and we'll come back with a free design
                concept &amp; quote within 24 hours.
              </p>

              <ul className="mt-8 space-y-3 text-sm text-zinc-300">
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 bg-teal-400" /> Auckland-based studio
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 bg-teal-400" /> Pasifika &amp; Māori-led design
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 bg-teal-400" /> Delivery across NZ
                </li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              <LeadFormCompact />
            </div>
          </div>
        </section>
      )}

      <Footer />
      <FloatingCTA />
    </main>
  );
}
